export interface AnswerAllocation {
  id: number
  monadicConceptId?: number
  options: { optionId: number; allocations: number }[]
  optionsSeen?: number[]
  optionsSeenNew?: number[]
  type: 'allocation'
}

export interface AnswerGaborGranger {
  id: number
  monadicConceptId?: number
  options: [{ scaleVal: number }]
  type: 'gaborGranger'
}

export interface AnswerIdeaPresenter {
  id: number
  monadicConceptId?: number
  options: { conceptId?: number; optionId: number }[]
  type: 'ideaPresenter'
}

export interface AnswerMatrix {
  id: number
  monadicConceptId?: number
  options:
    | {
        freeTextResponse?: string
        labels: string[]
        optionId: number
      }[]
    | { matrixOptions: { freeText: string; id: number }[]; optionId: number }[]
  optionsSeen?: number[]
  optionsSeenNew?: number[]
  type: 'matrix'
}

export interface AnswerMultipleChoice {
  id: number
  monadicConceptId?: number
  options: { freeTextResponse?: string; optionId: number }[]
  optionsSeen?: number[]
  optionsSeenNew?: number[]
  type: 'multipleChoice'
}

export interface AnswerOpenEnded {
  freeTextResponse: string
  id: number
  monadicConceptId?: number
  options: never[]
  type: 'openEnded'
}

export interface AnswerRanking {
  id: number
  monadicConceptId?: number
  options: { optionId: number; rank: number }[]
  optionsSeen?: number[]
  optionsSeenNew?: number[]
  type: 'ranking'
}

export interface AnswerScale {
  id: number
  monadicConceptId?: number
  options: { optionId: number; scaleVal: number }[]
  optionsSeen?: number[]
  optionsSeenNew?: number[]
  type: 'scale'
}

export interface Audience {
  audienceSlices: AudienceSlice[]
}

export interface AudienceSlice {
  audienceSliceCategories: AudienceSliceCategory[]
}

export interface AudienceSliceCategory {
  audienceSliceCategoryAttributes:
    | number[]
    | { end: number; start: number }[]
    | { enumValue: number; matrixOptionId: number }[]
    | { enumValue: number; numberRange: { end: number; start: number } }[]
  conceptId: number | null
  logicalModifier: LogicalModifier
  question: QuestionForAudience
  questionId: number
}

export interface Concept {
  audience: Audience | null
  description: string
  id: number
  media: MediaImage | MediaVideo
  preserved: boolean
}

export interface Disqualification {
  id: number
  questionId: number
  respondentId: number
  source: DisqualificationSource
  surveyId: number
  tactic: DisqualificationTactic
  waveId: number
}

export type DisqualificationSource = 'GLASS' | 'RESEARCH_DEFENDER'
export type DisqualificationTactic =
  | 'AUDIENCE_QUOTA'
  | 'BAD_RESPONDENT'
  | 'COPY_PASTE'
  | 'CUSTOM_QUALITY_SCREEN'
  | 'PRESURVEY_QUESTIONNAIRE'
  | 'QUESTION_QUOTA'
  | 'VARIABLE_QUOTA'
  | 'WAVE_QUOTA'

export interface EndMessage {
  subtitle?: string
  title: string
  type: 'disqualified' | 'closed' | 'completed'
}

export type FeatureCode = QuestionBlockFeatureCode | QuestionFeatureCode

export interface FeatureWithDescription<
  Code extends FeatureCode = FeatureCode,
> {
  code: Code
}

export type GaborGrangerFormat = 'CUSTOM' | 'DOLLARS' | 'PERCENT'
export type GaborGrangerObjective = 'MAX_SEEKING' | 'MIN_SEEKING'
export interface GaborGrangerSettings {
  format: GaborGrangerFormat
  formatCustomText: string | null
  increment: string
  max: string
  min: string
  objective: GaborGrangerObjective
  unitDecimals: number
}

export type Language = 'DE' | 'EN' | 'ES' | 'FR'

export type LogicalModifier = 'is' | 'isnt' | 'should'

export interface MatrixOption {
  id: number
  isAnchored: boolean
  isExclusive: boolean
  isFreeText: boolean
  sort: number
  title: string
}

export interface MediaImage {
  api_key: string
  asset_id: string
  bytes: number
  created_at: string
  etag: string
  folder: string
  format: string
  height: number
  placeholder: boolean
  public_id: string
  resource_type: 'image'
  secure_url: string
  signature: string
  tags: []
  type: 'upload'
  url: string
  version: number
  version_id: string
  width: number
}

export interface MediaVideo {
  asset_id: string
  audio: {
    bit_rate: string
    channel_layout: string
    channels: number
    codec: string
    frequency: number
  }
  bit_rate: number
  bytes: number
  created_at: string
  duration: number
  etag: string
  format: string
  frame_rate: number
  height: number
  is_audio: boolean
  nb_frames: number
  original_filename: string
  pages: number
  placeholder: boolean
  public_id: string
  resource_type: 'video'
  rotation: number
  secure_url: string
  signature: string
  tags: []
  type: 'upload'
  url: string
  version: number
  version_id: string
  video: {
    bit_rate: string
    codec: string
    dar: string
    level: number
    pix_format: string
    profile: string
    time_base: string
  }
  width: number
}

export interface Option {
  anchored: boolean
  carryOverParentId: number | null
  dataUrl: MediaImage | null
  exclusive: boolean
  high: number | null
  highLabel: string | null
  id: number
  isFreeTextOption: boolean
  low: number | null
  lowLabel: string | null
  middleLabel: string | null
  preserved: boolean
  questionOptionAudiences: { audience: Audience }[]
  scaleTypeId: number | null
  scaleUnitId: number | null
  sort: number
  step: number | null
  title: string
  viewRequired: boolean
}

export interface OptionImage extends Option {
  dataUrl: MediaImage
}

export interface OptionScale extends Option {
  high: number
  highLabel: string
  low: number
  lowLabel: string
  scaleTypeId: ScaleType
  scaleUnitId: ScaleUnit
  step: number
}

export interface OptionText extends Option {
  dataUrl: null
}

export interface Question {
  allocationSettings?: {
    carryOverTotalOptionId: number | null
    maxPerOption: number | null
    minPerOption: number | null
    step: number
    total: number | null
    units: 'CUSTOM' | 'DOLLARS' | 'PERCENT'
    useCarryOverOptionAsMax: boolean
  } | null
  blockId: number | null
  conceptTestMedia: Concept[]
  constraint?: QuestionConstraintNumber | QuestionConstraintRegex
  contentTypeId: number
  description: string
  displayOptionDescription: boolean
  gaborGrangerSettings: GaborGrangerSettings | null
  id: number
  isMonadicSettings: boolean
  isScaleBipolar: boolean
  isScreener: boolean
  isStandard: boolean
  label: string | null
  matrixOptions: MatrixOption[]
  monadicId: number | null
  options: Option[]
  questionAudiences: { audience: Audience }[]
  questionFeatures: (
    | QuestionFeature<QuestionFeatureCode>
    | QuestionFeatureWithNumberRange<QuestionFeatureCodeWithNumberRange>
  )[]
  questionQuotas: QuestionQuota[]
  questionType: { id: number; name: string }
  questionTypeId: number
  sort: number
  title: string
}

export interface QuestionBlock {
  children: QuestionBlockChild[]
  id: number
  isRandomized: boolean
  questionBlockFeatures: (
    | QuestionBlockFeature<QuestionBlockFeatureCode>
    | QuestionBlockFeatureWithNumberRange<QuestionBlockFeatureCodeWithNumberRange>
  )[]
  sequences: number | null
}

export interface QuestionBlockChild {
  id: number
  questionBlockAudiences: { audience: Audience }[]
  questions: { id: number }[]
  title: string
}

export type QuestionBlockFeature<Code extends QuestionBlockFeatureCode> = {
  feature: FeatureWithDescription<Code>
}

export type QuestionBlockFeatureCodeBase = ''
export type QuestionBlockFeatureCodeWithNumberRange = 'XOY02'
export type QuestionBlockFeatureCode =
  | QuestionBlockFeatureCodeBase
  | QuestionBlockFeatureCodeWithNumberRange

export interface QuestionBlockFeatureWithNumberRange<
  Code extends QuestionBlockFeatureCodeWithNumberRange,
> extends QuestionBlockFeature<Code> {
  numberRange: { end: number; start: number }
}

export interface QuestionConstraintNumber {
  id: number
  range: { end: number; start: number }
}

export interface QuestionConstraintRegex {
  errorMessage: string
  id: number
  regex: string
}

export type QuestionFeature<Code extends QuestionFeatureCode> = {
  enumRegex: string | null
  enumValue: number | null
  feature: FeatureWithDescription<Code>
  matrixOptionId: number | null
  regex: string | null
}

export type QuestionFeatureBase = QuestionFeature<QuestionFeatureCodeBase>

export interface QuestionFeatureWithNumberRange<
  Code extends QuestionFeatureCodeWithNumberRange,
> extends QuestionFeature<Code> {
  numberRange: { end: number; start: number }
}

export interface QuestionFeatureWithRegex<
  Code extends QuestionFeatureCodeWithRegex,
> extends QuestionFeature<Code> {
  regex: string
}

export type QuestionFeatureCodeBase =
  | 'COD01'
  | 'CON01'
  | 'COS01'
  | 'MRS01'
  | 'OM01'
  | 'OMI01'
  | 'OMS01'
  | 'QMCM01'
  | 'RDM01'
  | 'RDM02'
  | 'RMO'
  | 'SRS01'
  | 'VAL01'
  | 'VAL02'
  | 'VoxpopmeFeature02'
export type QuestionFeatureCodeWithNumberRange =
  | 'MCLL01'
  | 'MCUL01'
  | 'OPBLCK01'
  | 'QPC01'
  | 'SUM01'
export type QuestionFeatureCodeWithRegex = 'VoxpopmeFeature01'
export type QuestionFeatureCode =
  | QuestionFeatureCodeBase
  | QuestionFeatureCodeWithNumberRange
  | QuestionFeatureCodeWithRegex

export type QuestionForAudience = {
  conceptTestMedia: { id: number }[]
  monadicId: number | null
  questionTypeId: number
}

export interface QuestionQuota {
  disqualificationType: 'NON_QUALITY' | 'QUALITY'
  logicalModifier: 'all' | 'at_least' | 'at_most' | 'none'
  numberCompleted: number
  numberNeeded: number
  questionOptions?: { id: number }[]
  regex: string | null
}

export const SCALE_TYPES = {
  RADIO: 1,
  SLIDER: 2,
} as const
export type ScaleType = (typeof SCALE_TYPES)[keyof typeof SCALE_TYPES]

export const SCALE_UNITS = {
  DOLLARS: 2,
  NONE: 1,
  PERCENTAGES: 3,
} as const
export type ScaleUnit = (typeof SCALE_UNITS)[keyof typeof SCALE_UNITS]

export interface Survey {
  audience: Audience
  customizations: {
    buttonText: SurveyButtonText | null
    endMessages: EndMessage[]
  }
  hash: string
  id: number
  language: Language
  numberOfCompletes: number
  participants: number
  qualityChecks: SurveyQualityCheck[]
  questionBlocks: QuestionBlock[]
  questions: Question[]
  status: { id: number; name: 'approved' | 'completed' | 'draft' }
  surveyVariables: SurveyVariable[]
  title: string
  waveId: number
}

export interface SurveyButtonText {
  advance?: string
  done?: string
}

export interface SurveyQualityCheck {
  enabled: boolean
  type:
    | 'bad_respondent'
    | 'copy_paste'
    | 'duplicate'
    | 'presurvey_questionnaire'
}

export interface SurveyVariable {
  id: number
  quotas: SurveyVariableQuota[]
  segments: SurveyVariableSegment[]
  title: string
}

export interface SurveyVariableQuota {
  id: number
  numberCompleted: number
  numberNeeded: number
  type: 'all' | 'at_most' | 'none'
}

export interface SurveyVariableSegment {
  id: number
  questions: SurveyVariableSegmentQuestion[]
  quotaId: number | null
  title: string
}

export interface SurveyVariableSegmentQuestion {
  andGrouping: number
  constraints: SurveyVariableSegmentQuestionConstraint[]
  id: number
  logicalModifier: 'is' | 'isnt' | 'should'
  questionId: number
}

export interface SurveyVariableSegmentQuestionConstraint {
  id: number
  matrixOptionIds: number[] | null
  numberRange?: { end: number; start: number }
  optionId: number | null
}

export interface SurveyVariableSegmentQuestionConstraintMatrix
  extends SurveyVariableSegmentQuestionConstraint {
  matrixOptions: string[]
}

export interface SurveyVariableSegmentQuestionConstraintMatrixOptionIds
  extends SurveyVariableSegmentQuestionConstraint {
  matrixOptionIds: number[]
}

export interface SurveyVariableSegmentQuestionConstraintNumberRange
  extends SurveyVariableSegmentQuestionConstraint {
  numberRange: { end: number; start: number }
}

export interface SurveyVariableSegmentQuestionConstraintNumberRangeWithOptionID
  extends SurveyVariableSegmentQuestionConstraintNumberRange {
  optionId: number
}

export interface User {
  id: number
}
